import React from 'react';
import { useParams } from 'react-router-dom';
import { AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import DashboardContentWithRightSidebar from '@lib/layouts/DashboardLayout/DashboardContentWithRightSidebar/DashboardContentWithRightSidebar';
import TaskMenuBar from 'components/TaskMenuBar/TaskMenuBar';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { TaskViewMode } from './enums';
import TaskViewContainer from './TaskViewContainer';
import TaskFormViewContainer from './TaskFormViewContainer';
import TaskReportingViewContainer from './TaskReportingViewContainer';
import TaskInterventionsContainer from './TaskInterventionsContainer';
import TaskDepartmentsContainer from './TaskDepartmentsContainer';
import TaskReportingSidebar from './TaskReportingSidebar/TaskReportingSidebar';
import useReporting from './utils/useReporting';

interface Props {
  viewMode: TaskViewMode;
}

/**
 * Shared shell for the task tabs. Each tab is its own route, but they all
 * resolve to this component so React reconciles instead of remounting on a tab
 * click — only the container below the menu is swapped. Rendering `TaskMenuBar`
 * inside each tab's container instead would mount a fresh `TabBar` every time,
 * and its sliding active indicator can't animate across a remount. The same
 * reasoning is why the right sidebar layout stays mounted for every tab and
 * only drops its sidebar column (`withoutSidebar`) outside Reporting, instead
 * of being swapped for a bare `PageBody`.
 */
export default function TaskTabsPage({ viewMode }: Props) {
  const { id = '' } = useParams();
  const { getUserAccessByComponent, user }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.TasksManagement) ===
    AccessLevelEnum.Read;

  const withSidebar =
    viewMode === TaskViewMode.Reporting ||
    viewMode === TaskViewMode.Interventions;

  const {
    onStatusChangeHandler,
    refetchTask,
    taskData,
    taskError,
    taskLoading,
  } = useReporting({
    taskId: withSidebar ? id : undefined,
  });

  let content: React.ReactNode = null;

  if (viewMode === TaskViewMode.Details) {
    content = <TaskViewContainer />;
  } else if (viewMode === TaskViewMode.Form) {
    content = <TaskFormViewContainer />;
  } else if (viewMode === TaskViewMode.Reporting) {
    content = (
      <TaskReportingViewContainer
        taskData={taskData}
        taskError={taskError}
        taskLoading={taskLoading}
      />
    );
  } else if (viewMode === TaskViewMode.Interventions) {
    content = (
      <TaskInterventionsContainer
        onInterventionsChanged={refetchTask}
        taskData={taskData}
        taskLoading={taskLoading}
      />
    );
  } else if (viewMode === TaskViewMode.Departments) {
    content = <TaskDepartmentsContainer />;
  }

  return (
    <DashboardContentWithRightSidebar
      sidebar={
        <TaskReportingSidebar
          currentUserId={user?.id}
          isViewOnly={isViewOnly}
          onStatusChangeHandler={onStatusChangeHandler}
          taskData={taskData}
          taskLoading={taskLoading}
        />
      }
      useSidebarHeader
      toggleBtnTop={62.5}
      withoutSidebar={!withSidebar}
    >
      <PageBody withoutHeader>
        <TaskMenuBar id={id} />
        {content}
      </PageBody>
    </DashboardContentWithRightSidebar>
  );
}
